import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Shield,
  FileText,
  Award,
  CreditCard,
  Globe,
  Lock,
  Flag,
  Activity,
  ChevronRight,
  ChevronLeft,
  Check,
  Loader,
  Upload,
  Palette,
  Calendar,
  Users,
  Sparkles,
  Download,
  Eye
} from 'lucide-react'
import type { ComplianceFramework, PolicyGenerationSession, GeneratedPolicy } from '../lib/policy-generator-types'
import PolicyEditor from '../components/PolicyEditor'
import { useTenant } from '../contexts/TenantContext'

/**
 * AI-Powered Policy Generator Wizard
 *
 * Multi-step wizard for generating compliance policies:
 * Phase 1: Framework Selection
 * Phase 2: Business Context Questions
 * Phase 3: Branding Configuration
 * Phase 4: Policy Selection
 * Phase 5: Generation & PDF Creation
 * Phase 6: Publishing to Trust Center
 */

type WizardStep = 'frameworks' | 'context' | 'branding' | 'policies' | 'generate' | 'publish'

interface FrameworkOption {
  id: ComplianceFramework
  name: string
  description: string
  icon: React.ComponentType<any>
  policyCount: string
  color: string
  recommended?: boolean
}

const FRAMEWORK_OPTIONS: FrameworkOption[] = [
  {
    id: 'soc2',
    name: 'SOC 2 Type II',
    description: 'Trust Services Criteria for service organizations. Essential for SaaS companies.',
    icon: Shield,
    policyCount: '25-30 policies',
    color: 'blue',
    recommended: true
  },
  {
    id: 'hipaa',
    name: 'HIPAA',
    description: 'Healthcare data privacy and security. Required when handling PHI/ePHI.',
    icon: Activity,
    policyCount: '20-25 policies',
    color: 'red'
  },
  {
    id: 'iso27001',
    name: 'ISO 27001',
    description: 'International information security management standard.',
    icon: Award,
    policyCount: '35-40 policies',
    color: 'purple'
  },
  {
    id: 'pci-dss',
    name: 'PCI DSS',
    description: 'Payment card data security. Required for handling credit card information.',
    icon: CreditCard,
    policyCount: '15-20 policies',
    color: 'green'
  },
  {
    id: 'gdpr',
    name: 'GDPR',
    description: 'EU data protection regulation. Required for EU customers or operations.',
    icon: Globe,
    policyCount: '10-15 policies',
    color: 'indigo'
  },
  {
    id: 'ccpa',
    name: 'CCPA',
    description: 'California consumer privacy act. Required for California residents.',
    icon: Lock,
    policyCount: '8-12 policies',
    color: 'yellow'
  },
  {
    id: 'fedramp',
    name: 'FedRAMP',
    description: 'Federal cloud security authorization. Required for US government customers.',
    icon: Flag,
    policyCount: '40-50 policies',
    color: 'cyan'
  },
  {
    id: 'nist-csf',
    name: 'NIST CSF',
    description: 'Cybersecurity framework for critical infrastructure.',
    icon: FileText,
    policyCount: '20-25 policies',
    color: 'gray'
  },
  {
    id: 'custom',
    name: 'Custom Policy',
    description: 'Generate any custom policy using AI. Perfect for specific organizational needs.',
    icon: Sparkles,
    policyCount: 'As needed',
    color: 'pink'
  }
]

export default function PolicyGeneratorWizard() {
  const navigate = useNavigate()
  const { currentTenant } = useTenant()
  const [currentStep, setCurrentStep] = useState<WizardStep>('frameworks')
  const [loading, setLoading] = useState(false)
  const [session, setSession] = useState<PolicyGenerationSession | null>(null)

  // SECURITY FIX: Track all active timeouts to prevent memory leaks and state updates after unmount
  const timeoutsRef = useRef<Set<NodeJS.Timeout>>(new Set())

  // Helper function to create tracked timeout
  const createTimeout = (callback: () => void, delay: number): NodeJS.Timeout => {
    const timeoutId = setTimeout(() => {
      timeoutsRef.current.delete(timeoutId)
      callback()
    }, delay)
    timeoutsRef.current.add(timeoutId)
    return timeoutId
  }

  // Helper function to create tracked delay promise (for async/await usage)
  const createDelay = (ms: number): Promise<void> => {
    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        timeoutsRef.current.delete(timeoutId)
        resolve()
      }, ms)
      timeoutsRef.current.add(timeoutId)
    })
  }

  // Helper to get auth headers for API calls
  const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('auth-token')
    const headers: HeadersInit = { 'Content-Type': 'application/json' }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    return headers
  }

  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      // Clear all pending timeouts when component unmounts
      timeoutsRef.current.forEach(timeoutId => clearTimeout(timeoutId))
      timeoutsRef.current.clear()
    }
  }, [])

  // Scroll to top whenever step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [currentStep])

  // Auto-populate branding from tenant data when entering branding step if branding is empty
  useEffect(() => {
    if (currentStep === 'branding' && currentTenant && !brandingForm.companyName) {
      // SECURITY: Verify tenant ID matches session to prevent cross-tenant data leaks
      if (session && session.tenantId !== currentTenant.id) {
        console.error('🚨 SECURITY: Tenant mismatch detected in branding!', {
          sessionTenant: session.tenantId,
          currentTenant: currentTenant.id
        })
        return
      }

      console.log('📝 Auto-populating branding from tenant data', {
        tenantId: currentTenant.id,
        tenantName: currentTenant.name
      })

      const defaults = currentTenant.settings?.brandingDefaults

      setBrandingForm(prev => ({
        ...prev,
        companyName: currentTenant.name || prev.companyName,
        logo: currentTenant.settings?.logo || prev.logo,
        primaryColor: currentTenant.settings?.primaryColor || prev.primaryColor,
        footerText: defaults?.footerText || prev.footerText,
        reviewCycle: defaults?.reviewCycle || prev.reviewCycle,
        approvers: defaults?.approvers || prev.approvers
      }))

      // Set logo preview if tenant has a logo
      if (currentTenant.settings?.logo) {
        setLogoPreview(currentTenant.settings.logo)
      }
    }
  }, [currentStep, currentTenant, session])

  // Auto-populate business context from tenant defaults when entering context step
  useEffect(() => {
    if (currentStep === 'context' && currentTenant?.settings?.policyDefaults) {
      // SECURITY: Verify tenant ID matches session to prevent cross-tenant data leaks
      if (session && session.tenantId !== currentTenant.id) {
        console.error('🚨 SECURITY: Tenant mismatch detected!', {
          sessionTenant: session.tenantId,
          currentTenant: currentTenant.id
        })
        return
      }

      // Only auto-populate if fields are empty (new session or no data saved yet)
      if (!contextForm.companySize && currentTenant.settings.policyDefaults.companySize) {
        console.log('📝 Auto-populating business context from tenant defaults', {
          tenantId: currentTenant.id,
          tenantName: currentTenant.name
        })
        const defaults = currentTenant.settings.policyDefaults

        setContextForm(prev => ({
          ...prev,
          companySize: defaults.companySize || prev.companySize,
          industry: defaults.industry || prev.industry,
          productType: defaults.productType || prev.productType,
          infrastructure: defaults.infrastructure?.split(', ').filter(Boolean) || prev.infrastructure,
          developmentPractices: defaults.developmentPractices?.split(', ').filter(Boolean) || prev.developmentPractices
        }))
      }
    }
  }, [currentStep, currentTenant, session])

  // Load existing session on mount
  useEffect(() => {
    loadExistingSession()
  }, [])

  const loadExistingSession = async () => {
    try {
      const response = await fetch('/api/policy-generator/sessions', {
        method: 'GET',
        credentials: 'include',
        headers: getAuthHeaders()
      })

      if (!response.ok) return

      const data = await response.json()
      const sessions = data.sessions || []

      // Find most recent in-progress session
      const inProgressSession = sessions.find((s: PolicyGenerationSession) => s.status === 'in-progress')

      if (inProgressSession) {
        console.log('📥 Loading existing session:', inProgressSession.id)
        setSession(inProgressSession)

        // Populate frameworks
        if (inProgressSession.selectedFrameworks?.length > 0) {
          setSelectedFrameworks(inProgressSession.selectedFrameworks)
        }

        // Populate business context
        if (inProgressSession.businessContext) {
          const ctx = inProgressSession.businessContext
          setContextForm({
            companySize: ctx.companySize || '',
            industry: ctx.industry || '',
            industryOther: '',
            productType: ctx.productType || '',
            infrastructure: ctx.infrastructure?.split(', ').filter(Boolean) || [],
            infrastructureOther: '',
            developmentPractices: ctx.developmentPractices?.split(', ').filter(Boolean) || [],
            soc2TrustCriteria: ctx.frameworkSpecific?.soc2?.trustCriteria || [],
            soc2DataTypes: ctx.frameworkSpecific?.soc2?.dataTypes || [],
            soc2ThirdParties: ctx.frameworkSpecific?.soc2?.thirdParties || '',
            hipaaPhiTypes: ctx.frameworkSpecific?.hipaa?.phiTypes || [],
            hipaaCoveredEntityType: ctx.frameworkSpecific?.hipaa?.coveredEntityType || '',
            hipaaBusinessAssociates: ctx.frameworkSpecific?.hipaa?.businessAssociates || '',
            iso27001GovernanceStructure: ctx.frameworkSpecific?.iso27001?.governanceStructure || '',
            iso27001RiskMethodology: ctx.frameworkSpecific?.iso27001?.riskMethodology || '',
            pciProcessingModel: ctx.frameworkSpecific?.pci?.processingModel || '',
            pciLevel: ctx.frameworkSpecific?.pci?.level || ''
          })
        }

        // Populate branding
        if (inProgressSession.branding) {
          const brand = inProgressSession.branding
          setBrandingForm({
            logo: brand.logo || '',
            companyName: brand.companyName || '',
            primaryColor: brand.primaryColor || '#0066CC',
            footerText: brand.footerText || 'Confidential - Internal Use Only',
            effectiveDate: brand.effectiveDate || new Date().toISOString().split('T')[0],
            reviewCycle: brand.reviewCycle || 'annual',
            approvers: brand.approvers || []
          })

          // Load logo preview if logo exists
          if (brand.logo) {
            const logoUrl = `/api/policy-generator/upload-logo?key=${encodeURIComponent(brand.logo)}`
            setLogoPreview(logoUrl)
          }
        }

        // Always start at frameworks step, but with pre-populated data
        // This allows users to change framework selection (e.g., to Custom Policy)
        // while keeping their previous business context and branding settings
        setCurrentStep('frameworks')
      }
    } catch (error) {
      console.error('Failed to load existing session:', error)
      // Continue with fresh session on error
    }
  }

  // Phase 1: Framework Selection
  const [selectedFrameworks, setSelectedFrameworks] = useState<ComplianceFramework[]>([])

  // Phase 2: Business Context
  const [contextForm, setContextForm] = useState({
    // Universal questions
    companySize: '',
    industry: '',
    industryOther: '',
    productType: '',
    infrastructure: [] as string[],
    infrastructureOther: '',
    developmentPractices: [] as string[],

    // SOC 2 specific
    soc2TrustCriteria: [] as string[],
    soc2DataTypes: [] as string[],
    soc2ThirdParties: '',

    // HIPAA specific
    hipaaPhiTypes: [] as string[],
    hipaaCoveredEntityType: '',
    hipaaBusinessAssociates: '',

    // ISO 27001 specific
    iso27001GovernanceStructure: '',
    iso27001RiskMethodology: '',

    // PCI DSS specific
    pciProcessingModel: '',
    pciLevel: ''
  })

  // Phase 3: Branding Configuration
  const [brandingForm, setBrandingForm] = useState({
    logo: '',
    companyName: '',
    primaryColor: '#0066CC',
    footerText: 'Confidential - Internal Use Only',
    effectiveDate: new Date().toISOString().split('T')[0],
    reviewCycle: 'annual' as 'annual' | 'semi-annual' | 'quarterly',
    approvers: [] as string[]
  })
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string>('')

  // Phase 4: Policy Selection
  const [availableTemplates, setAvailableTemplates] = useState<any[]>([])
  const [selectedPolicyIds, setSelectedPolicyIds] = useState<string[]>([])
  const [customPolicyName, setCustomPolicyName] = useState('')
  const [customPolicyDescription, setCustomPolicyDescription] = useState('')

  // Phase 5: Generation Progress
  const [generating, setGenerating] = useState(false)
  const [generationProgress, setGenerationProgress] = useState({
    current: 0,
    total: 0,
    currentPolicyName: '',
    status: 'idle' as 'idle' | 'generating' | 'completed' | 'error'
  })

  // Phase 6: PDF Generation
  const [generatingPdfs, setGeneratingPdfs] = useState<Record<string, boolean>>({})
  const [policiesSynced, setPoliciesSynced] = useState(false)
  const [syncStatus, setSyncStatus] = useState<{ synced: number; total: number } | null>(null)

  // Phase 7: Policy Editing
  const [editingPolicy, setEditingPolicy] = useState<GeneratedPolicy | null>(null)

  // Phase 9: Publishing
  const [publishing, setPublishing] = useState(false)
  const [policiesToPublish, setPoliciesToPublish] = useState<string[]>([])
  const [publishSettings, setPublishSettings] = useState({
    gated: false,
    ndaRequired: true,  // Default: require NDA
    watermarked: true,  // Default: watermark PDFs
    visibility: 'gated' as 'public' | 'gated' | 'admin',  // Default: gated (private)
    category: 'Policies'
  })

  const toggleFramework = (framework: ComplianceFramework) => {
    setSelectedFrameworks(prev =>
      prev.includes(framework)
        ? prev.filter(f => f !== framework)
        : [...prev, framework]
    )
  }

  const toggleMultiSelect = (field: keyof typeof contextForm, value: string) => {
    setContextForm(prev => {
      const currentArray = prev[field] as string[]
      const newArray = currentArray.includes(value)
        ? currentArray.filter(v => v !== value)
        : [...currentArray, value]
      return { ...prev, [field]: newArray }
    })
  }

  const togglePolicySelection = (policyId: string) => {
    setSelectedPolicyIds(prev =>
      prev.includes(policyId)
        ? prev.filter(id => id !== policyId)
        : [...prev, policyId]
    )
  }

  // Fetch available templates when reaching policies step
  useEffect(() => {
    if (currentStep === 'policies' && selectedFrameworks.length > 0 && availableTemplates.length === 0) {
      // Skip fetching templates for custom framework (custom policies don't use templates)
      if (!selectedFrameworks.includes('custom')) {
        fetchTemplates()
      }
    }
  }, [currentStep])

  // Check if policies are already synced when viewing the generate step
  useEffect(() => {
    if (currentStep === 'generate' && session?.generatedPolicies && session.generatedPolicies.length > 0) {
      // If we have policies already in the session, they're already synced
      setPoliciesSynced(true)
    }
  }, [currentStep, session?.generatedPolicies.length])

  const fetchTemplates = async () => {
    try {
      const frameworks = selectedFrameworks.join(',')
      const response = await fetch(`/api/policy-generator/templates?frameworks=${frameworks}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      })

      if (response.ok) {
        const templates = await response.json()
        setAvailableTemplates(templates)

        // Auto-select all required policies
        const requiredIds = templates.filter((t: any) => t.required).map((t: any) => t.id)
        setSelectedPolicyIds(requiredIds)
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error)
    }
  }

  const proceedToGeneration = () => {
    // Determine which policy IDs to generate
    let policyIdsToGenerate: string[]

    // For custom policies, check if custom policy name is provided
    if (selectedFrameworks.includes('custom')) {
      if (!customPolicyName.trim()) {
        alert('Please provide a policy name')
        return
      }
      // Create a virtual policy ID for custom policy
      policyIdsToGenerate = [`custom-${Date.now()}`]
      setSelectedPolicyIds(policyIdsToGenerate)
    } else {
      if (selectedPolicyIds.length === 0) {
        alert('Please select at least one policy to generate')
        return
      }
      policyIdsToGenerate = selectedPolicyIds
    }

    setCurrentStep('generate')
    // Auto-start generation when entering the step (using tracked timeout)
    createTimeout(() => generatePolicies(policyIdsToGenerate), 500)
  }

  const generatePolicies = async (policyIds?: string[]) => {
    if (!session) return

    // Use provided policy IDs or fall back to selectedPolicyIds from state
    const policyIdsToGenerate = policyIds || selectedPolicyIds

    setGenerating(true)
    setPoliciesSynced(false)  // Reset sync status for new generation
    setSyncStatus(null)
    setGenerationProgress({
      current: 0,
      total: policyIdsToGenerate.length,
      currentPolicyName: 'Starting generation...',
      status: 'generating'
    })

    try {
      console.log(`🚀 Generating ${policyIdsToGenerate.length} policies sequentially...`)

      // Track failed policies
      const failedPolicies: Array<{ id: string; name: string; error: string }> = []

      // Generate policies one at a time
      for (let i = 0; i < policyIdsToGenerate.length; i++) {
        const policyId = policyIdsToGenerate[i]

        // Check if this is a custom policy
        const isCustomPolicy = policyId.startsWith('custom-')

        // Get template name for progress display
        const template = isCustomPolicy ? null : availableTemplates.find(t => t.id === policyId)
        const policyName = isCustomPolicy ? customPolicyName : (template?.policyName || 'Policy')

        // Show progress BEFORE generation starts (more accurate feedback)
        setGenerationProgress({
          current: i,
          total: policyIdsToGenerate.length,
          currentPolicyName: `Generating with AI: ${policyName}`,
          status: 'generating'
        })

        console.log(`  Generating ${i + 1}/${policyIdsToGenerate.length}: ${policyName}`)

        const requestBody: any = {
          sessionId: session.id,
          policyId
        }

        // Add custom policy details if this is a custom policy
        if (isCustomPolicy) {
          requestBody.customPolicy = {
            name: customPolicyName,
            description: customPolicyDescription
          }
        }

        const response = await fetch('/api/policy-generator/generate', {
          method: 'POST',
          credentials: 'include',
          headers: getAuthHeaders(),
          body: JSON.stringify(requestBody)
        })

        if (!response.ok) {
          const errorMessage = `HTTP ${response.status}: ${await response.text()}`
          console.error(`Failed to generate ${policyName}:`, errorMessage)
          failedPolicies.push({ id: policyId, name: policyName, error: errorMessage })
          // Continue with next policy even if one fails
          continue
        }

        const data = await response.json()
        console.log(`  ✅ Generated: ${policyName}`)

        // Update progress AFTER successful generation
        setGenerationProgress({
          current: i + 1,
          total: policyIdsToGenerate.length,
          currentPolicyName: `✓ Completed: ${policyName}`,
          status: 'generating'
        })

        // Update local session with new policy
        if (data.policy) {
          setSession(prev => {
            if (!prev) return prev
            const existingIndex = prev.generatedPolicies.findIndex(p => p.id === policyId)
            if (existingIndex >= 0) {
              prev.generatedPolicies[existingIndex] = data.policy
            } else {
              prev.generatedPolicies.push(data.policy)
            }
            return { ...prev }
          })
        }
      }

      // Calculate success count
      const successCount = policyIdsToGenerate.length - failedPolicies.length

      // All policies generated
      setGenerationProgress({
        current: policyIdsToGenerate.length,
        total: policyIdsToGenerate.length,
        currentPolicyName: 'Generating PDFs...',
        status: 'generating'
      })

      // Skip auto-PDF generation due to browser cache issues
      // Users will generate PDFs manually via the "Generate PDF" button
      console.log('✅ Policies generated. PDFs can be generated manually.')

      setGenerationProgress({
        current: policyIdsToGenerate.length,
        total: policyIdsToGenerate.length,
        currentPolicyName: 'All policies generated!',
        status: 'completed'
      })

      // Automatically select all generated policies for publishing
      setPoliciesToPublish(policyIdsToGenerate)

      // Show summary with failed policies if any
      if (failedPolicies.length > 0) {
        console.warn(`⚠️ Generated ${successCount} of ${policyIdsToGenerate.length} policies successfully`)
        console.warn('Failed policies:', failedPolicies)
        alert(`Generated ${successCount} of ${policyIdsToGenerate.length} policies successfully.\n\nFailed policies:\n${failedPolicies.map(p => `- ${p.name}`).join('\n')}`)
      } else {
        console.log(`✅ All ${policyIdsToGenerate.length} policies generated successfully with PDFs`)
      }

      // Start polling to check if all policies are synced to Blobs
      await checkPoliciesSynced(policyIdsToGenerate)

    } catch (error) {
      console.error('Generation failed:', error)
      setGenerationProgress(prev => ({
        ...prev,
        status: 'error'
      }))
      alert('Failed to generate policies. Please try again.')
    } finally {
      setGenerating(false)
    }
  }

  /**
   * Poll session endpoint to check if all policies are available in Blobs
   */
  const checkPoliciesSynced = async (policyIds?: string[]) => {
    if (!session) return

    // Use provided policy IDs or fall back to selectedPolicyIds from state
    const policyIdsToCheck = policyIds || selectedPolicyIds

    console.log('🔄 Checking if all policies are synced to storage...')
    setPoliciesSynced(false)
    setSyncStatus({ synced: 0, total: policyIdsToCheck.length })

    const maxAttempts = 20  // Try for up to 40 seconds (20 * 2s)
    const delayMs = 2000

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        // Fetch session from server to see which policies are available
        const response = await fetch(`/api/policy-generator/session/${session.id}`, {
          credentials: 'include',
          headers: getAuthHeaders()
        })
        if (!response.ok) {
          console.warn(`Failed to fetch session (attempt ${attempt})`)
          await createDelay(delayMs)
          continue
        }

        const refreshedSession: PolicyGenerationSession = await response.json()
        const syncedCount = refreshedSession.generatedPolicies.length

        setSyncStatus({ synced: syncedCount, total: policyIdsToCheck.length })
        console.log(`  📊 Synced ${syncedCount}/${policyIdsToCheck.length} policies`)

        // Check if all policies are now available
        if (syncedCount === policyIdsToCheck.length) {
          console.log('✅ All policies synced to storage!')
          setPoliciesSynced(true)
          setSession(refreshedSession)  // Update local session with all policies
          return
        }

        // Wait before next attempt
        if (attempt < maxAttempts) {
          await createDelay(delayMs)
        }
      } catch (error) {
        console.error(`Sync check failed (attempt ${attempt}):`, error)
        await createDelay(delayMs)
      }
    }

    // If we get here, not all policies synced in time
    console.warn('⚠️  Not all policies synced after polling. Some may still be propagating.')
    setPoliciesSynced(true)  // Enable button anyway after timeout
  }

  const generatePdfForPolicy = async (policyId: string, retries = 3, sessionData?: PolicyGenerationSession) => {
    const currentSession = sessionData || session
    if (!currentSession) return

    try {
      // Check if PDF already exists
      const policy = currentSession.generatedPolicies.find(p => p.id === policyId)
      const latestVersion = policy?.versions[policy.versions.length - 1]

      if (latestVersion?.pdfBlobKey) {
        console.log(`PDF already exists for ${policyId}`)
        return latestVersion.pdfBlobKey
      }

      console.log(`📄 Generating PDF for policy: ${policyId}`)

      // Retry loop with exponential backoff
      let lastError: Error | null = null
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          // Add cache-busting and random jitter to prevent HTTP/2 connection issues
          const cacheBuster = `${Date.now()}-${Math.random().toString(36).substring(7)}`
          const response = await fetch(`/api/policy-generator/pdf?_cb=${cacheBuster}`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              ...getAuthHeaders(),
              'Cache-Control': 'no-cache, no-store, must-revalidate'
            },
            body: JSON.stringify({
              sessionId: currentSession.id,
              policyId
            })
          })

          if (!response.ok) {
            // Try to get error details from response body
            let errorDetail = response.statusText
            try {
              const errorBody = await response.text()
              if (errorBody) {
                errorDetail = errorBody.substring(0, 200) // Limit error message length
                console.error(`[PDF Generation] Error response body:`, errorBody)
              }
            } catch {}
            console.error(`[PDF Generation] Failed with status ${response.status}:`, errorDetail)
            throw new Error(`HTTP ${response.status}: ${errorDetail}`)
          }

          const data = await response.json()
          const pdfBlobKey = data.pdfBlobKey

          // Update local session state
          if (policy && latestVersion) {
            latestVersion.pdfBlobKey = pdfBlobKey
            setSession({ ...currentSession })
          }

          return pdfBlobKey
        } catch (error) {
          lastError = error as Error

          if (attempt < retries) {
            // Longer delays for Netlify Blobs propagation: 8s, 16s, 32s, 64s
            const delayMs = Math.pow(2, attempt + 2) * 2000
            console.warn(`  ⚠️  Retry ${attempt}/${retries} for ${policyId} in ${(delayMs/1000).toFixed(0)}s (waiting for Netlify Blobs propagation)...`)
            await createDelay(delayMs)
          }
        }
      }

      throw lastError || new Error('Failed to generate PDF')
    } catch (error) {
      console.error(`Failed to generate PDF for ${policyId} after ${retries} attempts:`, error)
      throw error
    }
  }

  const generateAndDownloadPdf = async (policyId: string) => {
    if (!session) return

    setGeneratingPdfs(prev => ({ ...prev, [policyId]: true }))

    try {
      // Generate PDF if it doesn't exist
      const pdfBlobKey = await generatePdfForPolicy(policyId)

      // Download the PDF using fetch with authentication
      if (pdfBlobKey) {
        console.log(`📥 Downloading PDF: ${pdfBlobKey}`)

        const response = await fetch(`/api/policy-generator/download/${pdfBlobKey}`, {
          method: 'GET',
          credentials: 'include',
          headers: getAuthHeaders()
        })

        if (!response.ok) {
          const errorText = await response.text()
          throw new Error(`Download failed: ${errorText}`)
        }

        // Get the PDF blob
        const blob = await response.blob()

        // Extract filename from Content-Disposition header or use default
        const contentDisposition = response.headers.get('Content-Disposition')
        let filename = 'policy.pdf'
        if (contentDisposition) {
          const matches = /filename="(.+)"/.exec(contentDisposition)
          if (matches && matches[1]) {
            filename = matches[1]
          }
        }

        // Create a blob URL and trigger download
        const blobUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        // Clean up the blob URL
        URL.revokeObjectURL(blobUrl)
      }

    } catch (error) {
      console.error('PDF generation/download failed:', error)
      alert('Failed to generate or download PDF. Please try again.')
    } finally {
      setGeneratingPdfs(prev => ({ ...prev, [policyId]: false }))
    }
  }

  const generateAllPdfs = async () => {
    if (!session) return

    setGenerating(true)

    try {
      console.log('📄 Generating PDFs for all policies...')

      // Refresh session from server to ensure all policies are fully persisted
      console.log('  🔄 Refreshing session from server...')
      const sessionResponse = await fetch(`/api/policy-generator/session/${session.id}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      })
      if (!sessionResponse.ok) {
        throw new Error('Failed to refresh session')
      }
      const refreshedSession = await sessionResponse.json()
      setSession(refreshedSession)

      // Find all policies without PDFs
      const policiesNeedingPdfs = refreshedSession.generatedPolicies.filter((policy: any) => {
        const latestVersion = policy.versions[policy.versions.length - 1]
        return !latestVersion?.pdfBlobKey
      })

      console.log(`  Found ${policiesNeedingPdfs.length} policies needing PDFs`)

      // Initial delay to let Netlify Blobs fully propagate across CDN
      console.log('  ⏱️  Waiting 10 seconds for Netlify Blobs to propagate all policies...')
      await createDelay(10000)

      let successCount = 0
      let errorCount = 0

      for (let i = 0; i < policiesNeedingPdfs.length; i++) {
        const policy = policiesNeedingPdfs[i]

        try {
          console.log(`  [${i + 1}/${policiesNeedingPdfs.length}] Generating PDF for: ${policy.policyName}`)
          // Increase retries to 5 for better resilience against Blobs consistency issues
          await generatePdfForPolicy(policy.id, 5, refreshedSession)
          successCount++
          console.log(`    ✅ Success`)
        } catch (error) {
          errorCount++
          console.error(`    ❌ Failed after all retries: ${error}`)
          // Continue with remaining policies instead of stopping
        }

        // Add delay between requests to avoid browser connection issues
        // Wait 12 seconds (with random jitter) between each PDF generation
        if (i < policiesNeedingPdfs.length - 1) {
          // Add random jitter of ±2 seconds to break timing patterns
          const baseDelay = 12000
          const jitter = Math.random() * 4000 - 2000 // -2000ms to +2000ms
          const delay = Math.round(baseDelay + jitter)
          console.log(`    ⏱️  Waiting ${(delay / 1000).toFixed(1)} seconds before next PDF...`)
          await createDelay(delay)
        }
      }

      console.log(`✅ PDF generation complete: ${successCount} succeeded, ${errorCount} failed`)

      if (errorCount > 0) {
        alert(`Generated ${successCount} PDFs successfully.\n${errorCount} failed - please try generating those individually.`)
      } else {
        alert(`Successfully generated all ${successCount} PDFs!`)
      }

      // Refresh session to update PDF status badges with retry for eventual consistency
      console.log('🔄 Refreshing session to load PDF keys...')
      const maxRetries = 5
      const retryDelay = 2000

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        const finalSessionResponse = await fetch(`/api/policy-generator/session/${session.id}`, {
          credentials: 'include',
          headers: getAuthHeaders()
        })
        if (finalSessionResponse.ok) {
          const sessionData = await finalSessionResponse.json()
          const policiesWithPdf = sessionData.generatedPolicies.filter((p: any) =>
            p.versions[p.versions.length - 1]?.pdfBlobKey
          ).length

          console.log(`  Attempt ${attempt}: Found ${policiesWithPdf}/${successCount} policies with PDFs`)

          if (policiesWithPdf === successCount) {
            console.log('✅ All PDFs loaded in session!')
            setSession(sessionData)
            break
          }

          // Not all PDFs loaded yet, retry
          if (attempt < maxRetries) {
            console.log(`  ⏳ Waiting ${retryDelay}ms before retry...`)
            await createDelay(retryDelay)
          } else {
            // Final attempt - set session anyway
            console.warn('⚠️  Not all PDFs loaded after retries, setting session anyway')
            setSession(sessionData)
          }
        }
      }

    } catch (error) {
      console.error('Batch PDF generation failed:', error)
      alert('Failed to generate PDFs. Please try generating them individually.')
    } finally {
      setGenerating(false)
    }
  }

  const publishPolicies = async () => {
    if (!session || policiesToPublish.length === 0) {
      alert('Please select at least one policy to publish')
      return
    }

    setPublishing(true)

    try {
      console.log(`📤 Publishing ${policiesToPublish.length} policies to Trust Center`)

      const response = await fetch('/api/policy-generator/publish', {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          sessionId: session.id,
          policyIds: policiesToPublish,
          ...publishSettings
        })
      })

      if (!response.ok) {
        throw new Error('Failed to publish policies')
      }

      const data = await response.json()

      console.log(`✅ Publishing complete: ${data.successCount} published, ${data.errorCount} errors`)

      if (data.errorCount > 0) {
        // Show detailed error messages
        const errorMessages = data.errors.map((err: any) => `• ${err.policyName || err.policyId}: ${err.error}`).join('\n')
        const successMessage = data.successCount > 0 ? `Published ${data.successCount} policies successfully.\n\n` : ''
        alert(`${successMessage}${data.errorCount} failed:\n${errorMessages}`)
      } else {
        // Check if tenant has any deployments
        const deploymentsResponse = await fetch(`/api/deployments?tenantId=${session.tenantId}`, {
          credentials: 'include',
          headers: getAuthHeaders()
        })

        let hasDeployments = false
        if (deploymentsResponse.ok) {
          const deployments = await deploymentsResponse.json()
          hasDeployments = deployments && deployments.length > 0
        }

        if (!hasDeployments) {
          // No deployments yet - prompt to deploy first trust center
          const shouldDeploy = confirm(
            `Successfully published ${data.successCount} policies!\n\n` +
            `You haven't deployed your trust center yet. Would you like to go to the deployment page to publish your first trust center?\n\n` +
            `Click OK to go to deployment settings, or Cancel to stay here.`
          )

          if (shouldDeploy) {
            window.location.href = '/admin#trust-center-deployments'
            return
          }
        } else {
          alert(`Successfully published ${data.successCount} policies to your Trust Center!`)
        }
      }

      // Refresh session to get updated published status
      const sessionResponse = await fetch(`/api/policy-generator/session/${session.id}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      })
      if (sessionResponse.ok) {
        const sessionData = await sessionResponse.json()
        setSession(sessionData)
      }

      // Clear selection after publishing
      setPoliciesToPublish([])

    } catch (error) {
      console.error('Publishing failed:', error)
      alert('Failed to publish policies. Please try again.')
    } finally {
      setPublishing(false)
    }
  }

  const createSession = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      // If we have an existing session, update its frameworks instead of creating a new one
      // This preserves branding and context data when user changes framework selection
      if (session) {
        console.log('📝 Updating existing session frameworks:', selectedFrameworks)
        const response = await fetch(`/api/policy-generator/session/${session.id}/frameworks`, {
          method: 'PUT',
          credentials: 'include',
          headers,
          body: JSON.stringify({
            frameworks: selectedFrameworks
          })
        })

        if (!response.ok) {
          throw new Error('Failed to update session frameworks')
        }

        const data = await response.json()
        setSession(data)
        setCurrentStep('context')
        return
      }

      // No existing session, create a new one
      console.log('✨ Creating new session with frameworks:', selectedFrameworks)
      const response = await fetch('/api/policy-generator/session', {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          frameworks: selectedFrameworks
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        console.error('Failed to create session - response:', errorData)
        throw new Error(errorData.error || 'Failed to create session')
      }

      const data = await response.json()
      setSession(data.session)
      setCurrentStep('context')
    } catch (error) {
      console.error('Failed to create/update session:', error)
      alert('Failed to start policy generator. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const saveContext = async () => {
    if (!session) return

    // Validate required fields
    if (!contextForm.companySize || !contextForm.industry || !contextForm.productType) {
      alert('Please fill in all required fields')
      return
    }

    setLoading(true)
    try {
      // Build infrastructure list including custom "Other" value
      const infrastructureList = [...contextForm.infrastructure]
      if (contextForm.infrastructure.includes('Other') && contextForm.infrastructureOther) {
        // Replace "Other" with the actual custom value
        const index = infrastructureList.indexOf('Other')
        infrastructureList[index] = contextForm.infrastructureOther
      } else if (contextForm.infrastructure.includes('Other')) {
        // Remove "Other" if no custom value provided
        infrastructureList.splice(infrastructureList.indexOf('Other'), 1)
      }

      // Use custom industry value if "Other" is selected
      const industryValue = contextForm.industry === 'Other' && contextForm.industryOther
        ? contextForm.industryOther
        : contextForm.industry

      const response = await fetch(`/api/policy-generator/session/${session.id}/context`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          businessContext: {
            companySize: contextForm.companySize,
            industry: industryValue,
            productType: contextForm.productType,
            infrastructure: infrastructureList.join(', '),
            developmentPractices: contextForm.developmentPractices.join(', '),
            frameworkSpecific: {
              soc2: selectedFrameworks.includes('soc2') ? {
                trustCriteria: contextForm.soc2TrustCriteria,
                dataTypes: contextForm.soc2DataTypes,
                thirdParties: contextForm.soc2ThirdParties
              } : undefined,
              hipaa: selectedFrameworks.includes('hipaa') ? {
                phiTypes: contextForm.hipaaPhiTypes,
                coveredEntityType: contextForm.hipaaCoveredEntityType,
                businessAssociates: contextForm.hipaaBusinessAssociates
              } : undefined,
              iso27001: selectedFrameworks.includes('iso27001') ? {
                governanceStructure: contextForm.iso27001GovernanceStructure,
                riskMethodology: contextForm.iso27001RiskMethodology
              } : undefined,
              pci: selectedFrameworks.includes('pci-dss') ? {
                processingModel: contextForm.pciProcessingModel,
                level: contextForm.pciLevel
              } : undefined
            }
          }
        })
      })

      if (!response.ok) {
        throw new Error('Failed to save context')
      }

      const data = await response.json()
      setSession(data)

      // Also save to tenant settings as defaults for future policies
      // This reduces friction - users don't re-enter company data every time
      try {
        await fetch('/api/settings-db', {
          method: 'PUT',
          credentials: 'include',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            policyDefaults: {
              companySize: contextForm.companySize,
              industry: industryValue,
              productType: contextForm.productType,
              infrastructure: infrastructureList.join(', '),
              developmentPractices: contextForm.developmentPractices.join(', ')
            }
          })
        })
        console.log('✅ Saved business context as tenant defaults')
      } catch (err) {
        // Don't block user if saving defaults fails - just log it
        console.warn('⚠️ Failed to save tenant defaults:', err)
      }

      setCurrentStep('branding')
    } catch (error) {
      console.error('Failed to save context:', error)
      alert('Failed to save business context. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setLogoFile(file)
      // Create preview URL
      const reader = new FileReader()
      reader.onloadend = () => {
        setLogoPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const saveBranding = async () => {
    if (!brandingForm.companyName) {
      alert('Please enter your company name')
      return
    }

    setLoading(true)
    try {
      let logoKey = brandingForm.logo

      // Upload logo if a new file was selected
      if (logoFile) {
        const formData = new FormData()
        formData.append('logo', logoFile)

        const token = localStorage.getItem('auth-token')
        const headers: HeadersInit = {}
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }

        const uploadResponse = await fetch('/api/policy-generator/upload-logo', {
          method: 'POST',
          credentials: 'include',
          headers,
          body: formData
        })

        if (!uploadResponse.ok) {
          const errorText = await uploadResponse.text()
          const errorMessage = `Failed to upload logo (HTTP ${uploadResponse.status}): ${errorText}`
          console.error(errorMessage)
          throw new Error(errorMessage)
        }

        const uploadData = await uploadResponse.json()
        logoKey = uploadData.blobKey
      }

      // Save branding configuration
      const response = await fetch(`/api/policy-generator/session/${session!.id}/branding`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          branding: {
            logo: logoKey,
            companyName: brandingForm.companyName,
            primaryColor: brandingForm.primaryColor,
            footerText: brandingForm.footerText,
            effectiveDate: brandingForm.effectiveDate,
            reviewCycle: brandingForm.reviewCycle,
            approvers: brandingForm.approvers.filter(a => a.trim())
          }
        })
      })

      if (!response.ok) {
        throw new Error('Failed to save branding')
      }

      const data = await response.json()
      setSession(data)

      // Also save to tenant settings as defaults for future policies
      try {
        await fetch('/api/settings-db', {
          method: 'PUT',
          credentials: 'include',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            brandingDefaults: {
              footerText: brandingForm.footerText,
              reviewCycle: brandingForm.reviewCycle,
              approvers: brandingForm.approvers.filter(a => a.trim())
            }
          })
        })
        console.log('✅ Saved branding as tenant defaults')
      } catch (err) {
        // Don't block user if saving defaults fails
        console.warn('⚠️ Failed to save branding defaults:', err)
      }

      setCurrentStep('policies')
    } catch (error) {
      console.error('Failed to save branding:', error)
      alert('Failed to save branding configuration. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const getColorClasses = (color: string, selected: boolean) => {
    const colors = {
      blue: selected
        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500'
        : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50',
      red: selected
        ? 'border-red-500 bg-red-50 ring-2 ring-red-500'
        : 'border-gray-200 hover:border-red-300 hover:bg-red-50',
      purple: selected
        ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-500'
        : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50',
      green: selected
        ? 'border-green-500 bg-green-50 ring-2 ring-green-500'
        : 'border-gray-200 hover:border-green-300 hover:bg-green-50',
      indigo: selected
        ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-500'
        : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50',
      yellow: selected
        ? 'border-yellow-500 bg-yellow-50 ring-2 ring-yellow-500'
        : 'border-gray-200 hover:border-yellow-300 hover:bg-yellow-50',
      cyan: selected
        ? 'border-cyan-500 bg-cyan-50 ring-2 ring-cyan-500'
        : 'border-gray-200 hover:border-cyan-300 hover:bg-cyan-50',
      gray: selected
        ? 'border-gray-500 bg-gray-50 ring-2 ring-gray-500'
        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
    }
    return colors[color as keyof typeof colors] || colors.blue
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin')}
            className="text-sm text-gray-600 hover:text-gray-900 mb-4 flex items-center"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Admin
          </button>
          <h1 className="text-3xl font-bold text-gray-900">AI-Powered Policy Generator</h1>
          <p className="text-gray-600 mt-2">
            Generate comprehensive, customized security policies for your compliance frameworks
          </p>
        </div>

        {/* Step Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <StepIndicator
              step={1}
              label="Frameworks"
              active={currentStep === 'frameworks'}
              completed={['context', 'branding', 'policies', 'generate', 'publish'].includes(currentStep)}
            />
            <div className="flex-1 h-0.5 bg-gray-300 mx-2" />
            <StepIndicator
              step={2}
              label="Context"
              active={currentStep === 'context'}
              completed={['branding', 'policies', 'generate', 'publish'].includes(currentStep)}
            />
            <div className="flex-1 h-0.5 bg-gray-300 mx-2" />
            <StepIndicator
              step={3}
              label="Branding"
              active={currentStep === 'branding'}
              completed={['policies', 'generate', 'publish'].includes(currentStep)}
            />
            <div className="flex-1 h-0.5 bg-gray-300 mx-2" />
            <StepIndicator
              step={4}
              label="Policies"
              active={currentStep === 'policies'}
              completed={['generate', 'publish'].includes(currentStep)}
            />
            <div className="flex-1 h-0.5 bg-gray-300 mx-2" />
            <StepIndicator
              step={5}
              label="Generate"
              active={currentStep === 'generate'}
              completed={currentStep === 'publish'}
            />
            <div className="flex-1 h-0.5 bg-gray-300 mx-2" />
            <StepIndicator
              step={6}
              label="Publish"
              active={currentStep === 'publish'}
              completed={false}
            />
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          {currentStep === 'frameworks' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Select Compliance Frameworks
              </h2>
              <p className="text-gray-600 mb-6">
                Choose which compliance frameworks you need policies for. You can select multiple frameworks.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                {FRAMEWORK_OPTIONS.map((framework) => {
                  const Icon = framework.icon
                  const isSelected = selectedFrameworks.includes(framework.id)

                  return (
                    <button
                      key={framework.id}
                      onClick={() => toggleFramework(framework.id)}
                      className={`
                        relative p-6 rounded-lg border-2 transition-all text-left
                        ${getColorClasses(framework.color, isSelected)}
                      `}
                    >
                      {/* Recommended Badge */}
                      {framework.recommended && (
                        <div className="absolute top-3 right-3">
                          <span className="px-2 py-1 text-xs font-semibold bg-blue-500 text-white rounded">
                            Recommended
                          </span>
                        </div>
                      )}

                      {/* Checkmark */}
                      {isSelected && (
                        <div className="absolute top-3 left-3">
                          <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                            <Check className="h-4 w-4 text-white" />
                          </div>
                        </div>
                      )}

                      <div className="mt-8">
                        <Icon className="h-8 w-8 text-gray-700 mb-3" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {framework.name}
                        </h3>
                        <p className="text-sm text-gray-600 mb-3">
                          {framework.description}
                        </p>
                        <p className="text-xs text-gray-500 font-medium">
                          {framework.policyCount}
                        </p>
                      </div>
                    </button>
                  )
                })}
              </div>

              {/* Summary */}
              {selectedFrameworks.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <FileText className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-1">
                        {selectedFrameworks.length} framework{selectedFrameworks.length !== 1 ? 's' : ''} selected
                      </h4>
                      <p className="text-sm text-blue-700">
                        You've selected: {selectedFrameworks.map(f =>
                          FRAMEWORK_OPTIONS.find(opt => opt.id === f)?.name
                        ).join(', ')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between items-center">
                <button
                  onClick={() => navigate('/admin')}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={createSession}
                  disabled={selectedFrameworks.length === 0 || loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? (
                    <>
                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                      Creating Session...
                    </>
                  ) : (
                    <>
                      Continue
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {currentStep === 'context' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Business Context</h2>
              <p className="text-gray-600 mb-6">
                Help us understand your business to generate policies tailored to your needs.
              </p>

              <div className="space-y-8">
                {/* Universal Questions */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Size <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={contextForm.companySize}
                        onChange={(e) => setContextForm(prev => ({ ...prev, companySize: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">Select company size</option>
                        <option value="1-10">1-10 employees</option>
                        <option value="11-50">11-50 employees</option>
                        <option value="51-200">51-200 employees</option>
                        <option value="201-500">201-500 employees</option>
                        <option value="500+">500+ employees</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Industry <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={contextForm.industry}
                        onChange={(e) => setContextForm(prev => ({ ...prev, industry: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">Select industry</option>
                        <option value="SaaS">SaaS / Cloud Software</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Financial Services">Financial Services</option>
                        <option value="E-commerce">E-commerce / Retail</option>
                        <option value="Education">Education</option>
                        <option value="Government">Government</option>
                        <option value="Manufacturing">Manufacturing</option>
                        <option value="Professional Services">Professional Services</option>
                        <option value="Other">Other</option>
                      </select>
                      {contextForm.industry === 'Other' && (
                        <input
                          type="text"
                          value={contextForm.industryOther}
                          onChange={(e) => setContextForm(prev => ({ ...prev, industryOther: e.target.value }))}
                          placeholder="Specify your industry"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mt-2"
                          required
                        />
                      )}
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Product/Service <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={contextForm.productType}
                      onChange={(e) => setContextForm(prev => ({ ...prev, productType: e.target.value }))}
                      placeholder="e.g., Project management platform, Healthcare data analytics, etc."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Infrastructure / Service Provider (select all that apply)
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                      {['AWS', 'GCP', 'Azure', 'On-Premise', 'Hybrid', 'Multi-Cloud', 'Other'].map(infra => (
                        <button
                          key={infra}
                          type="button"
                          onClick={() => toggleMultiSelect('infrastructure', infra)}
                          className={`
                            px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all
                            ${contextForm.infrastructure.includes(infra)
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-300 bg-white text-gray-700 hover:border-blue-300'}
                          `}
                        >
                          {contextForm.infrastructure.includes(infra) && <Check className="h-4 w-4 inline mr-1" />}
                          {infra}
                        </button>
                      ))}
                    </div>
                    {contextForm.infrastructure.includes('Other') && (
                      <input
                        type="text"
                        value={contextForm.infrastructureOther}
                        onChange={(e) => setContextForm(prev => ({ ...prev, infrastructureOther: e.target.value }))}
                        placeholder="Specify other infrastructure or service"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    )}
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Development Practices (select all that apply)
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {['Agile', 'DevOps', 'CI/CD', 'Waterfall', 'Scrum', 'Kanban'].map(practice => (
                        <button
                          key={practice}
                          type="button"
                          onClick={() => toggleMultiSelect('developmentPractices', practice)}
                          className={`
                            px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all
                            ${contextForm.developmentPractices.includes(practice)
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-300 bg-white text-gray-700 hover:border-blue-300'}
                          `}
                        >
                          {contextForm.developmentPractices.includes(practice) && <Check className="h-4 w-4 inline mr-1" />}
                          {practice}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* SOC 2 Specific Questions */}
                {selectedFrameworks.includes('soc2') && (
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Shield className="h-5 w-5 mr-2 text-blue-600" />
                      SOC 2 Specific Questions
                    </h3>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Trust Services Criteria Focus
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {['Security', 'Availability', 'Confidentiality', 'Privacy', 'Processing Integrity'].map(criteria => (
                            <button
                              key={criteria}
                              type="button"
                              onClick={() => toggleMultiSelect('soc2TrustCriteria', criteria)}
                              className={`
                                px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all
                                ${contextForm.soc2TrustCriteria.includes(criteria)
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-gray-300 bg-white text-gray-700 hover:border-blue-300'}
                              `}
                            >
                              {contextForm.soc2TrustCriteria.includes(criteria) && <Check className="h-4 w-4 inline mr-1" />}
                              {criteria}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Customer Data Types
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {['PII', 'Financial Data', 'Health Data', 'Payment Info', 'Credentials', 'Business Data'].map(dataType => (
                            <button
                              key={dataType}
                              type="button"
                              onClick={() => toggleMultiSelect('soc2DataTypes', dataType)}
                              className={`
                                px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all
                                ${contextForm.soc2DataTypes.includes(dataType)
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-gray-300 bg-white text-gray-700 hover:border-blue-300'}
                              `}
                            >
                              {contextForm.soc2DataTypes.includes(dataType) && <Check className="h-4 w-4 inline mr-1" />}
                              {dataType}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Key Third-Party Service Providers
                        </label>
                        <textarea
                          value={contextForm.soc2ThirdParties}
                          onChange={(e) => setContextForm(prev => ({ ...prev, soc2ThirdParties: e.target.value }))}
                          placeholder="e.g., AWS (hosting), Stripe (payments), SendGrid (email)"
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* HIPAA Specific Questions */}
                {selectedFrameworks.includes('hipaa') && (
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Activity className="h-5 w-5 mr-2 text-red-600" />
                      HIPAA Specific Questions
                    </h3>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Types of PHI/ePHI Handled
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {['Patient Records', 'Lab Results', 'Prescriptions', 'Billing Info', 'Insurance Data', 'Clinical Notes'].map(phi => (
                            <button
                              key={phi}
                              type="button"
                              onClick={() => toggleMultiSelect('hipaaPhiTypes', phi)}
                              className={`
                                px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all
                                ${contextForm.hipaaPhiTypes.includes(phi)
                                  ? 'border-red-500 bg-red-50 text-red-700'
                                  : 'border-gray-300 bg-white text-gray-700 hover:border-red-300'}
                              `}
                            >
                              {contextForm.hipaaPhiTypes.includes(phi) && <Check className="h-4 w-4 inline mr-1" />}
                              {phi}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Covered Entity Type
                        </label>
                        <select
                          value={contextForm.hipaaCoveredEntityType}
                          onChange={(e) => setContextForm(prev => ({ ...prev, hipaaCoveredEntityType: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        >
                          <option value="">Select type</option>
                          <option value="Business Associate">Business Associate</option>
                          <option value="Healthcare Provider">Healthcare Provider</option>
                          <option value="Health Plan">Health Plan</option>
                          <option value="Healthcare Clearinghouse">Healthcare Clearinghouse</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Key Business Associates
                        </label>
                        <textarea
                          value={contextForm.hipaaBusinessAssociates}
                          onChange={(e) => setContextForm(prev => ({ ...prev, hipaaBusinessAssociates: e.target.value }))}
                          placeholder="List business associates with whom you share PHI"
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* ISO 27001 Specific Questions */}
                {selectedFrameworks.includes('iso27001') && (
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Award className="h-5 w-5 mr-2 text-purple-600" />
                      ISO 27001 Specific Questions
                    </h3>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Information Security Governance Structure
                        </label>
                        <textarea
                          value={contextForm.iso27001GovernanceStructure}
                          onChange={(e) => setContextForm(prev => ({ ...prev, iso27001GovernanceStructure: e.target.value }))}
                          placeholder="e.g., CISO reports to CEO, Security team of 5, quarterly security review board meetings"
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Risk Assessment Methodology
                        </label>
                        <select
                          value={contextForm.iso27001RiskMethodology}
                          onChange={(e) => setContextForm(prev => ({ ...prev, iso27001RiskMethodology: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                          <option value="">Select methodology</option>
                          <option value="NIST Risk Management Framework">NIST Risk Management Framework</option>
                          <option value="ISO 31000">ISO 31000</option>
                          <option value="OCTAVE">OCTAVE</option>
                          <option value="FAIR">FAIR (Factor Analysis of Information Risk)</option>
                          <option value="Custom">Custom methodology</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* PCI DSS Specific Questions */}
                {selectedFrameworks.includes('pci-dss') && (
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <CreditCard className="h-5 w-5 mr-2 text-green-600" />
                      PCI DSS Specific Questions
                    </h3>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Payment Processing Model
                        </label>
                        <select
                          value={contextForm.pciProcessingModel}
                          onChange={(e) => setContextForm(prev => ({ ...prev, pciProcessingModel: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          <option value="">Select model</option>
                          <option value="Payment Service Provider">Payment Service Provider (Stripe, etc.)</option>
                          <option value="Own Payment Gateway">Own Payment Gateway</option>
                          <option value="Hybrid">Hybrid</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          PCI DSS Level
                        </label>
                        <select
                          value={contextForm.pciLevel}
                          onChange={(e) => setContextForm(prev => ({ ...prev, pciLevel: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          <option value="">Select level</option>
                          <option value="Level 1">Level 1 (6M+ transactions/year)</option>
                          <option value="Level 2">Level 2 (1-6M transactions/year)</option>
                          <option value="Level 3">Level 3 (20K-1M e-commerce transactions/year)</option>
                          <option value="Level 4">Level 4 (&lt;20K e-commerce transactions/year)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center mt-8 pt-6 border-t">
                <button
                  onClick={() => setCurrentStep('frameworks')}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900 flex items-center"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </button>
                <button
                  onClick={saveContext}
                  disabled={loading || !contextForm.companySize || !contextForm.industry || !contextForm.productType}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? (
                    <>
                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Continue
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Branding Configuration */}
          {currentStep === 'branding' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Branding Configuration</h2>
              <p className="text-gray-600 mb-6">
                Customize how your policies will look when generated as PDFs.
              </p>

              <div className="space-y-6">
                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Logo
                  </label>
                  <div className="flex items-start space-x-4">
                    <div className="flex-1">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                          id="logo-upload"
                        />
                        <label htmlFor="logo-upload" className="cursor-pointer">
                          <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-600">
                            Click to upload or drag and drop
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            PNG, JPG, SVG up to 2MB
                          </p>
                        </label>
                      </div>
                    </div>
                    {logoPreview && (
                      <div className="w-32 h-32 border border-gray-200 rounded-lg p-2 flex items-center justify-center bg-gray-50">
                        <img src={logoPreview} alt="Logo preview" className="max-w-full max-h-full object-contain" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Company Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={brandingForm.companyName}
                    onChange={(e) => setBrandingForm(prev => ({ ...prev, companyName: e.target.value }))}
                    placeholder="Your Company Name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Used in PDF header and footer
                  </p>
                </div>

                {/* Primary Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Palette className="h-4 w-4 inline mr-2" />
                    Primary Brand Color
                  </label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="color"
                      value={brandingForm.primaryColor}
                      onChange={(e) => setBrandingForm(prev => ({ ...prev, primaryColor: e.target.value }))}
                      className="h-12 w-24 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={brandingForm.primaryColor}
                      onChange={(e) => setBrandingForm(prev => ({ ...prev, primaryColor: e.target.value }))}
                      placeholder="#0066CC"
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-sm text-gray-600">
                      This color will be used for headers and accents in your PDFs
                    </p>
                  </div>
                </div>

                {/* PDF Footer Configuration */}
                <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">PDF Footer Configuration</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Configure the three sections of the footer that appears at the bottom of every page
                  </p>

                  <div className="grid grid-cols-3 gap-4 mb-4">
                    {/* Left: Company Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                        Left Section
                      </label>
                      <input
                        type="text"
                        value={brandingForm.companyName || 'Company Name'}
                        disabled
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                      />
                      <p className="text-xs text-gray-500 mt-1">From company name above</p>
                    </div>

                    {/* Center: Classification */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                        Center Section
                      </label>
                      <input
                        type="text"
                        value={brandingForm.footerText}
                        onChange={(e) => setBrandingForm(prev => ({ ...prev, footerText: e.target.value }))}
                        placeholder="Document Classification"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">Classification text</p>
                    </div>

                    {/* Right: Page Numbers */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 uppercase">
                        Right Section
                      </label>
                      <input
                        type="text"
                        value="Page X of Y"
                        disabled
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Auto-generated</p>
                    </div>
                  </div>

                  {/* Footer Preview */}
                  <div className="border-t border-gray-300 pt-3 mt-3">
                    <p className="text-xs text-gray-500 mb-2">Preview:</p>
                    <div className="bg-white border border-gray-200 rounded p-3 text-xs text-gray-600 flex justify-between items-center">
                      <span className="flex-1 text-left">{brandingForm.companyName || 'Company Name'}</span>
                      <span className="flex-1 text-center font-semibold">{brandingForm.footerText || 'Classification'}</span>
                      <span className="flex-1 text-right">Page 1 of 5</span>
                    </div>
                  </div>
                </div>

                {/* Effective Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="h-4 w-4 inline mr-2" />
                    Policy Effective Date
                  </label>
                  <input
                    type="date"
                    value={brandingForm.effectiveDate}
                    onChange={(e) => setBrandingForm(prev => ({ ...prev, effectiveDate: e.target.value }))}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Review Cycle */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Policy Review Cycle
                  </label>
                  <select
                    value={brandingForm.reviewCycle}
                    onChange={(e) => setBrandingForm(prev => ({ ...prev, reviewCycle: e.target.value as any }))}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="annual">Annual</option>
                    <option value="semi-annual">Semi-Annual</option>
                    <option value="quarterly">Quarterly</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    How often policies will be reviewed and updated
                  </p>
                </div>

                {/* Approvers (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Users className="h-4 w-4 inline mr-2" />
                    Policy Approvers (Optional)
                  </label>
                  <div className="space-y-2">
                    {[0, 1, 2].map((index) => (
                      <input
                        key={index}
                        type="text"
                        value={brandingForm.approvers[index] || ''}
                        onChange={(e) => {
                          const newApprovers = [...brandingForm.approvers]
                          newApprovers[index] = e.target.value
                          setBrandingForm(prev => ({ ...prev, approvers: newApprovers }))
                        }}
                        placeholder={`Approver ${index + 1} (Name, Title)`}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Names and titles of people who approve these policies
                  </p>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center mt-8">
                <button
                  onClick={() => setCurrentStep('context')}
                  disabled={loading}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 flex items-center"
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back
                </button>
                <button
                  onClick={saveBranding}
                  disabled={loading || !brandingForm.companyName}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? (
                    <>
                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Continue
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Policy Selection */}
          {currentStep === 'policies' && (
            <div>
              {selectedFrameworks.includes('custom') ? (
                /* Custom Policy Input */
                <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    <Sparkles className="h-7 w-7 inline mr-2 text-purple-600" />
                    Custom Policy Generator
                  </h2>
                  <p className="text-gray-600 mb-6">
                    Describe the policy you want to generate. Our AI will create a comprehensive policy based on your requirements.
                  </p>

                  <div className="space-y-6 max-w-2xl">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Policy Name *
                      </label>
                      <input
                        type="text"
                        value={customPolicyName}
                        onChange={(e) => setCustomPolicyName(e.target.value)}
                        placeholder="e.g., Remote Work Security Policy, BYOD Policy, Social Media Policy"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Be specific about what policy you need
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Policy Description (Optional)
                      </label>
                      <textarea
                        value={customPolicyDescription}
                        onChange={(e) => setCustomPolicyDescription(e.target.value)}
                        rows={4}
                        placeholder="Provide additional details about what this policy should cover, specific requirements, or any constraints..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Add any specific requirements or guidelines for this policy
                      </p>
                    </div>

                    {customPolicyName && (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <Sparkles className="h-5 w-5 text-purple-600 mt-0.5 mr-3" />
                          <div>
                            <h4 className="font-semibold text-purple-900 mb-1">
                              Ready to generate: {customPolicyName}
                            </h4>
                            <p className="text-sm text-purple-700">
                              AI will create a comprehensive policy tailored to your organization
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Navigation */}
                  <div className="flex justify-between items-center mt-8">
                    <button
                      onClick={() => setCurrentStep('branding')}
                      className="px-4 py-2 text-gray-600 hover:text-gray-900 flex items-center"
                    >
                      <ChevronLeft className="h-4 w-4 mr-2" />
                      Back
                    </button>
                    <button
                      onClick={proceedToGeneration}
                      disabled={!customPolicyName.trim()}
                      className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      Generate Custom Policy
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </button>
                  </div>
                </>
              ) : (
                /* Standard Framework Policy Selection */
                <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Policies</h2>
                  <p className="text-gray-600 mb-6">
                    Choose which policies you want to generate. Required policies are pre-selected.
                  </p>

                  {availableTemplates.length === 0 ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader className="h-8 w-8 animate-spin text-blue-600" />
                      <span className="ml-3 text-gray-600">Loading available policies...</span>
                    </div>
                  ) : (
                <>
                  {/* Group policies by framework */}
                  {selectedFrameworks.map((framework) => {
                    const frameworkTemplates = availableTemplates.filter(t => t.framework === framework)
                    if (frameworkTemplates.length === 0) return null

                    const frameworkOption = FRAMEWORK_OPTIONS.find(f => f.id === framework)

                    return (
                      <div key={framework} className="mb-8">
                        <div className="flex items-center mb-4">
                          {frameworkOption && <frameworkOption.icon className="h-6 w-6 text-blue-600 mr-2" />}
                          <h3 className="text-lg font-semibold text-gray-900">
                            {frameworkOption?.name || framework}
                          </h3>
                          <span className="ml-3 text-sm text-gray-500">
                            ({frameworkTemplates.length} policies available)
                          </span>
                        </div>

                        {/* Group by category */}
                        {['security', 'privacy', 'operational', 'compliance'].map((category) => {
                          const categoryTemplates = frameworkTemplates.filter(t => t.category === category)
                          if (categoryTemplates.length === 0) return null

                          return (
                            <div key={category} className="mb-6">
                              <h4 className="text-sm font-medium text-gray-700 mb-3 uppercase">
                                {category}
                              </h4>
                              <div className="space-y-2">
                                {categoryTemplates.map((template) => {
                                  const isSelected = selectedPolicyIds.includes(template.id)
                                  const isRequired = template.required

                                  return (
                                    <button
                                      key={template.id}
                                      onClick={() => !isRequired && togglePolicySelection(template.id)}
                                      disabled={isRequired}
                                      className={`
                                        w-full p-4 rounded-lg border-2 text-left transition-all
                                        ${isSelected
                                          ? 'border-blue-500 bg-blue-50'
                                          : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                                        }
                                        ${isRequired ? 'opacity-75' : 'cursor-pointer'}
                                      `}
                                    >
                                      <div className="flex items-start">
                                        <div className="flex-shrink-0 mt-1">
                                          <div className={`
                                            w-5 h-5 rounded border-2 flex items-center justify-center
                                            ${isSelected
                                              ? 'border-blue-500 bg-blue-500'
                                              : 'border-gray-300 bg-white'
                                            }
                                          `}>
                                            {isSelected && <Check className="h-3 w-3 text-white" />}
                                          </div>
                                        </div>
                                        <div className="ml-3 flex-1">
                                          <div className="flex items-center">
                                            <h5 className="font-semibold text-gray-900">
                                              {template.policyName}
                                            </h5>
                                            {isRequired && (
                                              <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                                                Required
                                              </span>
                                            )}
                                            <span className="ml-auto text-xs text-gray-500">
                                              {template.estimatedLength === 'short' && '2-3 pages'}
                                              {template.estimatedLength === 'medium' && '4-6 pages'}
                                              {template.estimatedLength === 'long' && '7+ pages'}
                                            </span>
                                          </div>
                                          <p className="text-sm text-gray-600 mt-1">
                                            {template.description}
                                          </p>
                                          {template.dependencies && template.dependencies.length > 0 && (
                                            <p className="text-xs text-gray-500 mt-1">
                                              Depends on: {template.dependencies.map((depId: string) =>
                                                availableTemplates.find(t => t.id === depId)?.policyName
                                              ).join(', ')}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    </button>
                                  )
                                })}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )
                  })}

                  {/* Summary */}
                  {selectedPolicyIds.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                      <div className="flex items-start">
                        <FileText className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
                        <div>
                          <h4 className="font-semibold text-blue-900 mb-1">
                            {selectedPolicyIds.length} {selectedPolicyIds.length === 1 ? 'policy' : 'policies'} selected
                          </h4>
                          <p className="text-sm text-blue-700">
                            Estimated total: {selectedPolicyIds.length * 2}-{selectedPolicyIds.length * 4} minutes to generate
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Navigation */}
                  <div className="flex justify-between items-center">
                    <button
                      onClick={() => setCurrentStep('branding')}
                      className="px-4 py-2 text-gray-600 hover:text-gray-900 flex items-center"
                    >
                      <ChevronLeft className="h-4 w-4 mr-2" />
                      Back
                    </button>
                    <button
                      onClick={proceedToGeneration}
                      disabled={selectedPolicyIds.length === 0}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      Generate Policies
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </button>
                  </div>
                  </>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 5: AI Generation & Results */}
          {currentStep === 'generate' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {generationProgress.status === 'completed' ? 'Policies Generated!' : 'Generating Policies'}
              </h2>
              <p className="text-gray-600 mb-6">
                {generationProgress.status === 'generating' && 'Claude AI is generating your customized policies...'}
                {generationProgress.status === 'completed' && 'Your policies have been successfully generated and are ready for review.'}
                {generationProgress.status === 'error' && 'An error occurred during generation. Please try again.'}
              </p>

              {/* Progress Indicator */}
              {generating && (
                <div className="mb-8">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <Sparkles className="h-6 w-6 text-blue-600 animate-pulse mr-3" />
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-700">
                            {generationProgress.currentPolicyName}
                          </span>
                          <span className="text-sm text-gray-600">
                            {generationProgress.current} / {generationProgress.total}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                            style={{
                              width: `${(generationProgress.current / generationProgress.total) * 100}%`
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">
                      This may take a few minutes. Each policy is being customized to your business context and compliance requirements.
                    </p>
                  </div>
                </div>
              )}

              {/* Generated Policies List */}
              {generationProgress.status === 'completed' && session?.generatedPolicies && (
                <div className="space-y-4 mb-8">
                  {session.generatedPolicies.map((policy) => (
                    <div
                      key={policy.id}
                      className="border border-gray-200 rounded-lg p-5 hover:border-blue-300 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <div className="flex items-center">
                              <Check className="h-5 w-5 text-green-500 mr-2" />
                              <h3 className="text-lg font-semibold text-gray-900">
                              {policy.policyName}
                            </h3>
                            <span className="ml-3 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded uppercase">
                              {policy.framework}
                            </span>
                            <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded">
                              {policy.category}
                            </span>
                            {/* PDF Status Badge */}
                            {policy.versions[policy.versions.length - 1]?.pdfBlobKey ? (
                              <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded flex items-center">
                                <Check className="h-3 w-3 mr-1" />
                                PDF Ready
                              </span>
                            ) : (
                              <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded flex items-center">
                                <Download className="h-3 w-3 mr-1" />
                                PDF Needed
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-2">
                            Generated {new Date(policy.generatedAt).toLocaleString()} • Version {policy.currentVersion}
                            {!policy.versions[policy.versions.length - 1]?.pdfBlobKey && (
                              <span className="text-yellow-600 font-medium"> • Click "Create PDF" to create</span>
                            )}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            {policy.versions[0]?.markdownContent.length.toLocaleString()} characters
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setEditingPolicy(policy)}
                            className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Preview & Edit
                          </button>
                          <button
                            onClick={() => generateAndDownloadPdf(policy.id)}
                            disabled={generatingPdfs[policy.id] || !policiesSynced}
                            className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                          >
                            {generatingPdfs[policy.id] ? (
                              <>
                                <Loader className="h-4 w-4 mr-1 animate-spin" />
                                Generating...
                              </>
                            ) : !policiesSynced ? (
                              <>
                                <Loader className="h-4 w-4 mr-1 animate-spin" />
                                Syncing...
                              </>
                            ) : policy.versions[policy.versions.length - 1]?.pdfBlobKey ? (
                              <>
                                <Download className="h-4 w-4 mr-1" />
                                Download PDF
                              </>
                            ) : (
                              <>
                                <Sparkles className="h-4 w-4 mr-1" />
                                Create PDF
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Actions */}
              {generationProgress.status === 'completed' && (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start">
                      <Check className="h-5 w-5 text-green-600 mt-0.5 mr-3" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-green-900 mb-1">
                          Generation Complete!
                        </h4>
                        <p className="text-sm text-green-700">
                          All {session?.generatedPolicies.length} policies have been generated successfully.
                          {(() => {
                            const withPdfs = session?.generatedPolicies.filter(p => p.versions[p.versions.length - 1]?.pdfBlobKey).length || 0
                            const total = session?.generatedPolicies.length || 0
                            if (withPdfs === total) {
                              return ' All PDFs are ready! Click "Next: Publish" below to publish them to your Trust Center.'
                            } else if (!policiesSynced) {
                              return ` Syncing policies to storage before PDFs can be created...`
                            } else {
                              return ` Create PDFs for all policies to continue.`
                            }
                          })()}
                        </p>

                        {/* Sync Status Indicator */}
                        {syncStatus && !policiesSynced && (
                          <div className="mt-3 flex items-center text-sm text-blue-600">
                            <Loader className="h-4 w-4 mr-2 animate-spin" />
                            <span>
                              Syncing policies to storage... ({syncStatus.synced}/{syncStatus.total} ready)
                            </span>
                          </div>
                        )}

                        {(() => {
                          const withPdfs = session?.generatedPolicies.filter(p => p.versions[p.versions.length - 1]?.pdfBlobKey).length || 0
                          const total = session?.generatedPolicies.length || 0
                          if (withPdfs < total) {
                            return (
                              <button
                                onClick={generateAllPdfs}
                                disabled={generating || !policiesSynced}
                                className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm"
                              >
                                {generating ? (
                                  <>
                                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                                    Creating PDFs...
                                  </>
                                ) : !policiesSynced ? (
                                  <>
                                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                                    Syncing policies...
                                  </>
                                ) : (
                                  <>
                                    <Sparkles className="h-4 w-4 mr-2" />
                                    Create All PDFs
                                  </>
                                )}
                              </button>
                            )
                          }
                          return null
                        })()}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Navigation */}
              <div className="flex justify-between items-center">
                <button
                  onClick={() => setCurrentStep('policies')}
                  disabled={generating}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 flex items-center"
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back to Selection
                </button>
                {generationProgress.status === 'completed' && (
                  <div className="flex space-x-3">
                    <button
                      onClick={generatePolicies}
                      className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center"
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      Regenerate All
                    </button>
                    {(() => {
                      const withPdfs = session?.generatedPolicies.filter(p => p.versions[p.versions.length - 1]?.pdfBlobKey).length || 0
                      const total = session?.generatedPolicies.length || 0
                      if (withPdfs === total) {
                        return (
                          <button
                            onClick={() => setCurrentStep('publish')}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                          >
                            Next: Publish
                            <ChevronRight className="h-4 w-4 ml-2" />
                          </button>
                        )
                      }
                      return null
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 6: Publish to Trust Center */}
          {currentStep === 'publish' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Publish to Trust Center
              </h2>
              <p className="text-gray-600 mb-6">
                Select which policies you want to publish to your Trust Center and configure their visibility settings.
              </p>

              {/* Policy Selection List */}
              {session?.generatedPolicies && (
                <div className="space-y-4 mb-6">
                  {session.generatedPolicies.map((policy) => (
                    <div
                      key={policy.id}
                      className="border border-gray-200 rounded-lg p-5 hover:border-blue-300 transition-colors"
                    >
                      <div className="flex items-start space-x-3">
                        {/* Publish checkbox */}
                        <input
                          type="checkbox"
                          checked={policiesToPublish.includes(policy.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setPoliciesToPublish(prev => [...prev, policy.id])
                            } else {
                              setPoliciesToPublish(prev => prev.filter(id => id !== policy.id))
                            }
                          }}
                          className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {policy.policyName}
                            </h3>
                            <span className="ml-3 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded uppercase">
                              {policy.framework}
                            </span>
                            <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded">
                              {policy.category}
                            </span>
                            {/* PDF Status Badge */}
                            <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded flex items-center">
                              <Check className="h-3 w-3 mr-1" />
                              PDF Ready
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-2">
                            Version {policy.currentVersion} • {policy.versions[0]?.markdownContent.length.toLocaleString()} characters
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Publishing Controls */}
              {session && session.generatedPolicies.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                  {/* Selection Controls */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={policiesToPublish.length === session.generatedPolicies.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setPoliciesToPublish(() => session.generatedPolicies.map(p => p.id))
                            } else {
                              setPoliciesToPublish([])
                            }
                          }}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Select All</span>
                      </label>
                      <span className="text-sm text-gray-600">
                        {policiesToPublish.length} of {session.generatedPolicies.length} selected for publishing
                      </span>
                    </div>
                  </div>

                  {/* Publishing Settings */}
                  <div className="bg-white rounded-lg p-4 mb-4 space-y-3">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Publishing Settings</h3>

                    {/* Visibility */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-2">Visibility</label>
                      <div className="flex space-x-4">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            checked={publishSettings.visibility === 'public'}
                            onChange={() => setPublishSettings(prev => ({ ...prev, visibility: 'public' }))}
                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">Public</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            checked={publishSettings.visibility === 'gated'}
                            onChange={() => setPublishSettings(prev => ({ ...prev, visibility: 'gated' }))}
                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">Gated (requires access request)</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            checked={publishSettings.visibility === 'admin'}
                            onChange={() => setPublishSettings(prev => ({ ...prev, visibility: 'admin' }))}
                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">Admin Only</span>
                        </label>
                      </div>
                    </div>

                    {/* Additional Options */}
                    <div className="flex space-x-6">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={publishSettings.watermarked}
                          onChange={(e) => setPublishSettings(prev => ({ ...prev, watermarked: e.target.checked }))}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Watermark PDFs</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={publishSettings.ndaRequired}
                          onChange={(e) => setPublishSettings(prev => ({ ...prev, ndaRequired: e.target.checked }))}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Require NDA</span>
                      </label>
                    </div>

                    {/* Category */}
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-2">Category</label>
                      <input
                        type="text"
                        value={publishSettings.category}
                        onChange={(e) => setPublishSettings(prev => ({ ...prev, category: e.target.value }))}
                        placeholder="e.g., Policies, Security Policies"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Publish Button */}
                  <div className="flex justify-end">
                    <button
                      onClick={publishPolicies}
                      disabled={publishing || policiesToPublish.length === 0}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {publishing ? (
                        <>
                          <Loader className="h-4 w-4 mr-2 animate-spin" />
                          Publishing...
                        </>
                      ) : (
                        <>
                          <FileText className="h-4 w-4 mr-2" />
                          Publish {policiesToPublish.length} to Trust Center
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between items-center">
                <button
                  onClick={() => setCurrentStep('generate')}
                  disabled={publishing}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 flex items-center"
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back to PDFs
                </button>
                <button
                  onClick={() => navigate('/admin')}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                >
                  Done
                  <ChevronRight className="h-4 w-4 ml-2" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Policy Editor Modal */}
      {editingPolicy && session && (
        <PolicyEditor
          policy={editingPolicy}
          sessionId={session.id}
          onClose={() => setEditingPolicy(null)}
          onUpdate={(updatedPolicy) => {
            // Update the policy in the session
            const policyIndex = session.generatedPolicies.findIndex(p => p.id === updatedPolicy.id)
            if (policyIndex !== -1) {
              session.generatedPolicies[policyIndex] = updatedPolicy
              setSession({ ...session })
            }
            setEditingPolicy(updatedPolicy)
          }}
        />
      )}
    </div>
  )
}

function StepIndicator({
  step,
  label,
  active,
  completed
}: {
  step: number
  label: string
  active: boolean
  completed: boolean
}) {
  return (
    <div className="flex flex-col items-center">
      <div
        className={`
          w-10 h-10 rounded-full flex items-center justify-center font-semibold
          ${active ? 'bg-blue-600 text-white' : completed ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'}
        `}
      >
        {completed ? <Check className="h-5 w-5" /> : step}
      </div>
      <span className={`text-xs mt-2 ${active ? 'text-blue-600 font-semibold' : 'text-gray-600'}`}>
        {label}
      </span>
    </div>
  )
}
